import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Waves, Anchor, Sun, MapPin, Music, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { CommunityNav } from "@/components/community/CommunityNav";

const SITE = "https://lagooncityrealestate.ca";

const SECTIONS = [
  { icon: Waves, tag: "The Canals", title: "A home with water at your back door", img: "/assets/community/canals-aerial.jpg",
    body: "Lagoon City is Ontario's original canal community — kilometres of sheltered, navigable waterways winding between the homes and out to Lake Simcoe. Many properties come with their own private dock, so your boat is moored steps from the kitchen. It's the rare place in Ontario where waterfront living means literally living on the water." },
  { icon: Anchor, tag: "Boating & Marina", title: "Direct access to Lake Simcoe & the Trent-Severn", img: "/assets/community/grand-harbour-aerial.jpg",
    body: "From the canals you can idle out to Lake Simcoe and connect to the Trent-Severn Waterway — a boater's dream. The full-service Grand Harbour Marina offers slip and boatel rentals, fuel and marine services right in the community, plus the waterfront Dockyard Grill for lunch off the water.", reverse: true },
  { icon: Sun, tag: "Beaches & Parks", title: "A private beach, parks and lakeside playgrounds", img: "/assets/community/private-beach.jpg",
    body: "Residents enjoy a private sandy community beach on Lake Simcoe, open green beach-park lawns, and lakeside playgrounds for the kids. Summers are spent swimming, paddleboarding and watching the sunset over the water; winters bring ice-fishing and snowmobiling on one of Ontario's most beloved lakes.",
    extras: [
      { src: "/assets/community/welcome-sign.jpg", title: "The Welcome Sign", caption: "Ontario's best kept secret — you'll see it on your way in" },
      { src: "/assets/community/playground.jpg", title: "Lakeside Playground", caption: "Swings and slides with a shoreline view" },
    ] },
];

const HERO_FACTS = [
  { icon: Music, label: "Live Entertainment", value: "Casino Rama — 15 minutes away" },
  { icon: Waves, label: "On the shores of", value: "Lake Simcoe, connecting to the Trent-Severn Waterway" },
  { icon: MapPin, label: "Nearest town", value: "Orillia — minutes away for major shopping, dining, entertainment & more" },
  { icon: Anchor, label: "Best known", value: "Scenic canals that let you step right from your backyard into your boat, kayak or canoe" },
  { icon: Clock, label: "Nearby — within 5 minutes", value: "Waterfront restaurants, full-service marina, pharmacy, cafés, grocery, LCBO, gas & Tim Hortons" },
];

export default function Neighbourhood() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>The Neighbourhood — Living in Lagoon City, Ontario | Joselle Alves</title>
        <meta name="description" content="Discover the Lagoon City waterfront community — private canals, boat docks, a full-service marina, private beach, parks and everything close by." />
        <link rel="canonical" href={`${SITE}/community/neighbourhood`} />
      </Helmet>
      <PageHeader />
      <CommunityNav />

      <section className="relative overflow-hidden">
        <div className="absolute -right-40 top-0 w-[30rem] h-[30rem] rounded-full bg-[#E890A7]/8 blur-[120px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12 sm:pt-20 sm:pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4">Community &amp; Local Business</p>
            <h1 data-testid="neighbourhood-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              The neighbourhood <em className="italic text-[#E890A7]">you&apos;re buying into</em>
            </h1>
            <p className="mt-6 max-w-4xl text-slate-300 font-light text-sm sm:text-base leading-relaxed">
              Tucked along the shores of Lake Simcoe, Lagoon City is a one-of-a-kind waterfront community where private
              canals, boat docks and lakefront living come together. Step outside your door and the water is part of
              everyday life—from a morning paddle along the canals to cruising Lake Simcoe and connecting to the
              Trent-Severn Waterway. Add a full-service marina, local businesses that know your name and a welcoming
              community where familiar faces become friends, and you&apos;ll discover there&apos;s much more to Lagoon
              City than a property listing—it&apos;s a lifestyle.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="neighbourhood-facts">
              {HERO_FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.label} className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 backdrop-blur-md p-5 hover:border-[#E890A7]/40 transition-colors duration-300">
                    <Icon size={18} className="text-[#E890A7] mb-3" />
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E890A7]/70">{f.label}</p>
                    <p className="mt-2 text-slate-200 font-light text-sm leading-snug">{f.value}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 pt-6 pb-20 space-y-24">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.tag} className="space-y-10">
              <motion.section initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${s.reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="rounded-3xl overflow-hidden border border-[#E890A7]/12">
                  <img src={s.img} alt={s.title} className="w-full h-72 sm:h-96 object-cover" />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4 flex items-center gap-2"><Icon size={14} /> {s.tag}</p>
                  <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight">{s.title}</h2>
                  <p className="mt-5 text-slate-400 font-light leading-relaxed">{s.body}</p>
                </div>
              </motion.section>
              {s.extras && (
                <div className="grid sm:grid-cols-2 gap-6">
                  {s.extras.map((e) => (
                    <figure key={e.title} className="group relative rounded-3xl overflow-hidden border border-[#E890A7]/12 hover:border-[#E890A7]/40 transition-colors duration-500">
                      <img src={e.src} alt={e.title} className="w-full h-64 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      <figcaption className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/85 via-black/30 to-transparent">
                        <p className="font-display text-2xl text-white leading-tight">{e.title}</p>
                        <p className="text-sm text-slate-300 font-light mt-1">{e.caption}</p>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Footer />
    </div>
  );
}
